import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { HintProps, positionClose } from './index';
import { helperTypographyText, helperGetShadowColor, helperViewHintList } from '../../helpers';
import { Container as Button } from '../../common/Button/styles';

export const animateHint = (direction: 'left' | 'right' | 'top' | 'bottom' | 'fade') => keyframes`
  0% {
    opacity: 0;
    ${
      direction !== 'fade' &&
      css`
        transform: translate(
          ${(direction === 'left' && '-1rem') || (direction === 'right' && '1rem') || '-50%'},
          ${(direction === 'top' && '-1rem') || (direction === 'bottom' && '1rem') || '-50%'}
        );
      `
    };
  }
  100% {
    opacity: 1;
    ${
      direction !== 'fade' &&
      css`
        transform: translate(
          ${direction === 'left' || (direction === 'right' && '0') || '-50%'},
          ${direction === 'top' || (direction === 'bottom' && '0') || '-50%'},
          0
        );
      `
    };
  }
`;

const getPadding = (position: HintProps['position'], isImportant: HintProps['isImportant']) => {
  if (isImportant) {
    return '3.2rem 2rem 1.8rem';
  }

  switch (position) {
    case 'default':
    case 'bottom-static':
      return '1.1rem 2rem';

    case 'absolute':
      return '1.1rem 2rem 1rem';

    default:
      return '0.7rem 2rem 0.5rem';
  }
};

const hintVertical = css`
  position: absolute;
  left: 50%;
  max-width: 30rem;
  width: max-content;
  text-align: center;
  transform: translate(-50%, 0);
`;

const hintHorizontal = css`
  position: absolute;
  top: 50%;
  width: max-content;
  max-width: 41.9rem;
  transform: translate(0, -50%);
`;

const arrowVertical = (view: HintProps['view']) => css`
  content: '';
  position: absolute;
  z-index: -1;
  width: 1.5rem;
  height: 1.5rem;
  background-color: ${helperViewHintList[view!].backgroundColor};
  transform: rotate(45deg);
  left: 0;
  right: 0;
  margin: 0 auto;
`;

const arrowHorizontal = css`
  position: absolute;
  z-index: -1;
  top: 0;
`;

export const arrowHorizontalLeft = css`
  ${arrowHorizontal};
  right: 100%;
  margin-right: -0.5rem;
`;

export const arrowHorizontalRight = css`
  ${arrowHorizontal};
  left: 100%;
  margin-left: -0.5rem;
`;

export const HintSkin = styled('div')<HintProps>`
  position: relative;
  z-index: 1;
  border-radius: ${props => (props.position !== 'default' ? '1rem !important' : '1rem')};

  ${props => helperViewHintList[props.view!]};

  ${({ view, position }) => {
    switch (position) {
      case 'bottom-static':
        return css`
          &:after {
            content: '';
            position: absolute;
            right: 0.9rem;
            bottom: calc(100% - 0.4rem);
            width: 0;
            height: 0;
            border-width: 0.8rem;
            border-style: solid;
            border-color: transparent ${helperViewHintList[view!].backgroundColor}
              ${helperViewHintList[view!].backgroundColor} transparent;
            transform: rotate(12deg);
          }
        `;

      case 'top':
        return css`
          &:after {
            ${arrowVertical(view)};
            top: calc(100% - 0.7rem);
          }
        `;

      case 'bottom':
        return css`
          &:after {
            ${arrowVertical(view)};
            bottom: calc(100% - 0.7rem);
          }
        `;

      case 'left':
        return css`
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        `;

      case 'right':
        return css`
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        `;

      default:
        return '';
    }
  }};
`;

export const HintBackdrop = styled(HintSkin)<HintProps>`
  position: absolute;
  left: 0;
  right: 0;
  top: 1px;
  bottom: -1px;
  color: ${props => helperGetShadowColor(props.view)};
  background-color: ${props => helperGetShadowColor(props.view)};

  &:after {
    background-color: ${props => helperGetShadowColor(props.view)};
  }
`;

export const HintContainer = styled('div')<HintProps>`
  position: relative;
  z-index: ${props => props.position !== 'default' && props.position !== 'bottom-static' && 100};
  margin-top: ${props => props.isImportant && '4.3rem'};
  transform: translate(0, 0);
  animation-duration: 0.3s;
  animation-fill-mode: forwards;

  ${props =>
    helperTypographyText({
      size: 'normal',
      lh: props.isImportant ? 'normal' : 'small'
    })};

  ${props => {
    switch (props.position) {
      case 'top':
        return css`
          ${hintVertical};
          bottom: calc(100% + 1.5rem);
          animation-name: ${animateHint('top')};
        `;

      case 'bottom':
        return css`
          ${hintVertical};
          top: calc(100% + 1.5rem);
          animation-name: ${animateHint('bottom')};
        `;

      case 'left':
        return css`
          ${hintHorizontal};
          right: calc(100% + 3.7rem);
          animation-name: ${animateHint('left')};
        `;

      case 'right':
        return css`
          ${hintHorizontal};
          left: calc(100% + 3.7rem);
          animation-name: ${animateHint('right')};
        `;

      case 'absolute':
        return css`
          position: absolute;
          width: max-content;
          max-width: 32.4rem;
          animation-name: ${animateHint('fade')};
        `;

      default:
        return css`
          position: relative;
          animation-name: ${animateHint('fade')};
        `;
    }
  }};

  ${({ view, isImportant }) =>
    isImportant &&
    css`
      text-align: center;

      &:before {
        content: '';
        position: absolute;
        z-index: 2;
        left: 0;
        right: 0;
        bottom: calc(100% - 2.2rem);
        width: 7.2rem;
        height: 7.2rem;
        margin-left: auto;
        margin-right: auto;
        background-image: url("data:image/svg+xml,%3Csvg width='72' height='72' viewBox='0 0 72 72' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23fff' d='M31 29h10v26H31z'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M69 63L36 6 3 63h66zm-30-9h-6v-6h6v6zm-6-12h6V30h-6v12z' fill='${encodeURIComponent(
          !view || view === 'warning' ? '#593800' : '#000'
        )}'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: 50%;
        background-size: 7.2rem 7.2rem;
      }
    `};
`;

export const HintInner = styled('div')<HintProps>`
  box-sizing: content-box;
  display: flex;
  align-items: center;
  padding: ${props => getPadding(props.position, props.isImportant)};
  padding-left: ${props => props.position === 'right' && 0};
  padding-right: ${props => props.position === 'left' && 0};
  min-height: ${({ position }) =>
    position !== 'default' && position !== 'bottom-static' && position !== 'absolute' && '3.6rem'};
  text-align: left;
`;

export const Icon = styled('div')`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  min-height: 3.6rem;
  margin: 0.5rem 1.2rem 0.5rem 0.3rem;

  img,
  svg {
    vertical-align: middle;
  }
`;

interface ButtonCloseProps {
  closePosition?: typeof positionClose[number];
}

export const ButtonClose = styled('div')<ButtonCloseProps>`
  align-self: ${props => props.closePosition === 'top' && 'flex-start'};
  position: relative;
  margin-left: 1rem;
  margin-right: ${props => (props.closePosition === 'top' ? '-1.5rem' : '-0.5rem')};
  margin-top: ${props => props.closePosition === 'top' && '-0.6rem'};

  ${Button} {
    padding: 0;
    color: inherit !important;
    transition-property: background-color, border-color, box-shadow;
  }
`;

export const HintCover = styled('div')`
  position: relative;
`;
